import React, { useState } from 'react';

const IntegrationHub = () => {
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);

  const integrations = [
    {
      id: 'github',
      name: 'GitHub',
      icon: '🐙',
      status: 'available',
      description: 'Connect GitHub for code reviews and PR notifications',
      features: [
        'PR notifications in Slack',
        'Code review requests',
        'Deployment tracking',
        'Issue management'
      ],
      config: {
        repo: '',
        token: '',
        events: ['push', 'pull_request', 'issues']
      }
    },
    {
      id: 'jira',
      name: 'Jira',
      icon: '📋',
      status: 'available',
      description: 'Sync Jira tickets and updates',
      features: [
        'Ticket creation from Slack',
        'Status updates',
        'Sprint tracking',
        'Automated workflows'
      ],
      config: {
        url: '',
        project: '',
        apiKey: ''
      }
    },
    {
      id: 'notion',
      name: 'Notion',
      icon: '📝',
      status: 'coming_soon',
      description: 'Sync with Notion databases and pages',
      features: [
        'Database queries',
        'Page creation',
        'Content updates',
        'Knowledge base search'
      ],
      config: {
        workspace: '',
        apiKey: ''
      }
    },
    {
      id: 'google',
      name: 'Google Workspace',
      icon: '📊',
      status: 'available',
      description: 'Connect Google Docs, Sheets, and Calendar',
      features: [
        'Calendar scheduling',
        'Document creation',
        'Sheet updates',
        'Drive file management'
      ],
      config: {
        clientId: '',
        scope: []
      }
    },
    {
      id: 'stripe',
      name: 'Stripe',
      icon: '💳',
      status: 'available',
      description: 'Payment notifications and customer management',
      features: [
        'Payment alerts',
        'Customer updates',
        'Subscription management',
        'Revenue reporting'
      ],
      config: {
        apiKey: '',
        webhookSecret: ''
      }
    },
    {
      id: 'datadog',
      name: 'Datadog',
      icon: '📈',
      status: 'coming_soon',
      description: 'Monitor metrics and alerts',
      features: [
        'Metric monitoring',
        'Alert notifications',
        'Dashboard snapshots',
        'Incident management'
      ],
      config: {
        apiKey: '',
        appKey: ''
      }
    },
    {
      id: 'openai',
      name: 'OpenAI',
      icon: '🤖',
      status: 'experimental',
      description: 'Advanced AI capabilities beyond Claude',
      features: [
        'GPT-4 integration',
        'DALL-E image generation',
        'Code completion',
        'Advanced analysis'
      ],
      config: {
        apiKey: '',
        model: 'gpt-4'
      }
    },
    {
      id: 'zapier',
      name: 'Zapier',
      icon: '⚡',
      status: 'available',
      description: 'Connect to 5000+ apps',
      features: [
        'Multi-app workflows',
        'Custom triggers',
        'Data transformation',
        'Scheduled tasks'
      ],
      config: {
        apiKey: '',
        zaps: []
      }
    }
  ];

  const workflows = [
    {
      name: 'Code Review Pipeline',
      integrations: ['github', 'slack', 'claude'],
      description: 'Automated code review with AI assistance',
      steps: [
        'GitHub PR created',
        'Claude analyzes code',
        'Posts review to Slack',
        'Updates PR status'
      ]
    },
    {
      name: 'Customer Support Flow',
      integrations: ['slack', 'jira', 'stripe', 'claude'],
      description: 'Handle support tickets with context',
      steps: [
        'Customer message in Slack',
        'Check Stripe customer data',
        'Create Jira ticket',
        'Claude suggests solution'
      ]
    },
    {
      name: 'Daily Standup Automation',
      integrations: ['slack', 'google', 'notion', 'claude'],
      description: 'Collect and summarize daily updates',
      steps: [
        'Schedule via Google Calendar',
        'Collect updates in Slack',
        'Claude summarizes',
        'Save to Notion'
      ]
    },
    {
      name: 'Incident Response',
      integrations: ['datadog', 'slack', 'jira', 'phone'],
      description: 'Rapid incident response system',
      steps: [
        'Datadog alert triggers',
        'Create Slack channel',
        'Open Jira incident',
        'Call on-call engineer'
      ]
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-900 text-green-300';
      case 'coming_soon': return 'bg-yellow-900 text-yellow-300';
      case 'experimental': return 'bg-purple-900 text-purple-300';
      default: return 'bg-gray-700 text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Available Integrations */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Available Integrations</h3>
        <div className="grid md:grid-cols-4 gap-3">
          {integrations.map((integration) => (
            <button
              key={integration.id}
              onClick={() => setSelectedIntegration(integration.id)}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedIntegration === integration.id
                  ? 'bg-green-900 border-green-400'
                  : 'bg-black border-gray-600 hover:border-gray-500'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-2xl">{integration.icon}</span>
                <span className={`text-xs px-2 py-1 rounded ${getStatusColor(integration.status)}`}>
                  {integration.status.replace('_', ' ')}
                </span>
              </div>
              <div className="font-bold text-yellow-400">{integration.name}</div>
              <div className="text-xs text-gray-400 mt-1">{integration.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Integration Details */}
      {selectedIntegration && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">
            {integrations.find(i => i.id === selectedIntegration)?.name} Integration
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-yellow-400 mb-3">Features</h4>
              <ul className="space-y-2">
                {integrations.find(i => i.id === selectedIntegration)?.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span className="text-sm text-gray-400">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-yellow-400 mb-3">Configuration</h4>
              <div className="space-y-3">
                {Object.entries(integrations.find(i => i.id === selectedIntegration)?.config || {}).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-500 mb-1 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </label>
                    <input
                      type={key.includes('Key') || key.includes('Secret') ? 'password' : 'text'}
                      placeholder={Array.isArray(value) ? value.join(', ') : String(value)}
                      className="w-full bg-black text-green-400 px-3 py-2 rounded border border-gray-600 focus:border-green-400 focus:outline-none text-sm"
                    />
                  </div>
                ))}
              </div>
              <button className="mt-4 px-4 py-2 bg-green-600 text-black rounded-lg hover:bg-green-500 transition-colors font-bold w-full">
                Connect {integrations.find(i => i.id === selectedIntegration)?.name}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-built Workflows */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Pre-built Workflows</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {workflows.map((workflow) => (
            <div key={workflow.name} className="bg-black p-4 rounded border border-gray-600">
              <h4 className="font-bold text-yellow-400 mb-2">{workflow.name}</h4>
              <p className="text-sm text-gray-400 mb-3">{workflow.description}</p>
              <div className="mb-3">
                <div className="text-xs text-gray-500 mb-1">Uses:</div>
                <div className="flex flex-wrap gap-1">
                  {workflow.integrations.map((int) => (
                    <span key={int} className="text-xs px-2 py-1 bg-gray-800 rounded">
                      {int}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Steps:</div>
                <ol className="space-y-1">
                  {workflow.steps.map((step, idx) => (
                    <li key={idx} className="text-xs text-gray-400 flex items-start">
                      <span className="text-green-400 mr-1">{idx + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
              <button className="mt-3 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-500 transition-colors">
                Use Template
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Connection Status */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Active Connections</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-black p-3 rounded">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Slack API</span>
            </div>
            <span className="text-xs text-gray-400">Connected via OAuth</span>
          </div>
          <div className="flex items-center justify-between bg-black p-3 rounded">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Claude AI</span>
            </div>
            <span className="text-xs text-gray-400">Native integration</span>
          </div>
          <div className="flex items-center justify-between bg-black p-3 rounded">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Email (Resend)</span>
            </div>
            <span className="text-xs text-gray-400">API Key configured</span>
          </div>
          <div className="flex items-center justify-between bg-black p-3 rounded">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Voice (Twilio)</span>
            </div>
            <span className="text-xs text-gray-400">Phone number active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationHub;